// src/pages/LavorazioniGenerate.jsx
//
// Proposte di lavorazione ricavate dalle mail della casella assistenza@.
// Qui si confronta quello che ha letto il modello con la mail originale e
// i suoi allegati (anteprima in pagina), si apre il form di creazione
// precompilato (che resta quello di sempre), si scarta la proposta oppure
// si risponde al cliente con una bozza gia' pronta.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Badge, Card } from '../components/ui';

const API_BASE = '/api';

const TABS = [
  { key: 'da_revisionare', label: 'Da revisionare' },
  { key: 'convertita', label: 'Convertite' },
  { key: 'scartata', label: 'Scartate' },
  { key: 'mail', label: 'Mail non riconosciute' },
];

const MAIL_FILTERS = [
  { key: 'not_work', label: 'Non richieste di lavoro' },
  { key: 'error', label: 'Analisi fallita' },
  { key: 'received', label: 'In attesa di analisi' },
];

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const senderLabel = (mail) => {
  const from = mail?.from || {};
  if (from.name && from.address) return `${from.name} <${from.address}>`;
  return from.address || from.name || 'mittente sconosciuto';
};

const addressList = (list) => (list || []).map((a) => a.address || a.name).filter(Boolean).join(', ');

const userLabel = (u) => {
  if (!u) return '';
  const full = `${u.nome || ''} ${u.cognome || ''}`.trim();
  return full || u.username || '';
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const previewKind = (att) => {
  const ct = String(att.contentType || '').toLowerCase();
  const name = String(att.filename || '').toLowerCase();
  if (ct === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(name)) return 'image';
  return null;
};

const typeBadge = (type) => {
  if (type === 'intervento') return 'bg-sky-100 text-sky-700';
  if (type === 'manutenzione') return 'bg-emerald-100 text-emerald-700';
  return 'bg-gray-100 text-gray-600';
};

const urgencyBadge = (u) => {
  if (u === 'alta') return 'bg-red-100 text-red-700';
  if (u === 'normale') return 'bg-amber-100 text-amber-700';
  if (u === 'bassa') return 'bg-gray-100 text-gray-600';
  return '';
};

const confidenceBadge = (c) => {
  const pct = Math.round((c || 0) * 100);
  if (pct >= 80) return { cls: 'bg-emerald-100 text-emerald-700', label: `Lettura ${pct}%` };
  if (pct >= 50) return { cls: 'bg-amber-100 text-amber-700', label: `Lettura ${pct}%` };
  return { cls: 'bg-red-100 text-red-700', label: `Lettura ${pct}%` };
};

const matchLabel = (match) => {
  if (!match?.customerUserId) return 'Cliente non trovato in anagrafica';
  const by = {
    email: 'per email del mittente',
    companyName: 'per ragione sociale o macchina',
    phone: 'per telefono',
    manual: 'scelto a mano',
    history: 'come le mail precedenti di questo mittente',
  }[match.customerMatchedBy] || '';
  return `Cliente: ${userLabel(match.customerUserId)} ${by}`.trim();
};

const Field = ({ label, value }) =>
  value ? (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 break-words">{value}</div>
    </div>
  ) : null;

const btn = {
  gray: 'px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 disabled:opacity-50',
  green: 'px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50',
  sky: 'px-3 py-1.5 rounded-lg bg-sky-100 text-sky-700 text-xs font-medium hover:bg-sky-200 disabled:opacity-50',
  skySolid: 'px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-medium hover:bg-sky-700 disabled:opacity-50',
  red: 'px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-xs font-medium hover:bg-red-200 disabled:opacity-50',
  amber: 'px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-xs font-medium hover:bg-amber-200 disabled:opacity-50',
  indigo: 'px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50',
};

export default function LavorazioniGenerate() {
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || '';
  const isManager = ['admin', 'segreteria', 'gestione'].includes(role);

  const [tab, setTab] = useState('da_revisionare');
  const [mailFilter, setMailFilter] = useState('not_work');
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showExtracted, setShowExtracted] = useState({});

  // filtri dell'elenco
  const [filters, setFilters] = useState({ from: '', to: '', q: '', customer: '' });
  const [pendingQ, setPendingQ] = useState('');

  // assegnazione del cliente dal dettaglio
  const [clientUsers, setClientUsers] = useState([]);
  const [assign, setAssign] = useState({ customerUserId: '', applyToSender: true });
  const [assignBusy, setAssignBusy] = useState(false);

  // anteprime allegati: id allegato -> object URL
  const [previews, setPreviews] = useState({});
  const previewUrls = useRef({});

  // risposta al cliente
  const [reply, setReply] = useState(null); // { from, dedicated, defaults, draft, replies }
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyForm, setReplyForm] = useState({ to: '', cc: '', subject: '', text: '' });
  const [replyBusy, setReplyBusy] = useState(false);

  const revokePreviews = useCallback(() => {
    Object.values(previewUrls.current).forEach((u) => {
      try {
        window.URL.revokeObjectURL(u);
      } catch {
        // gia' revocato
      }
    });
    previewUrls.current = {};
    setPreviews({});
  }, []);

  useEffect(() => revokePreviews, [revokePreviews]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/generated-work/summary`);
      if (res && res.ok) setSummary(await res.json());
    } catch {
      // il riepilogo e' solo un contorno
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      if (filters.from) qs.set('from', filters.from);
      if (filters.to) qs.set('to', filters.to);
      if (filters.q) qs.set('q', filters.q);
      if (tab === 'mail') {
        qs.set('status', mailFilter);
        const res = await authFetch(`${API_BASE}/generated-work/inbound?${qs}`);
        if (!res || !res.ok) throw new Error('Errore nel caricamento delle mail');
        setMails(await res.json());
      } else {
        qs.set('status', tab);
        if (filters.customer) qs.set('customer', filters.customer);
        const res = await authFetch(`${API_BASE}/generated-work?${qs}`);
        if (!res || !res.ok) throw new Error('Errore nel caricamento delle proposte');
        setItems(await res.json());
      }
    } catch (err) {
      setError(err.message || 'Errore di caricamento');
    } finally {
      setLoading(false);
    }
  }, [tab, mailFilter, filters]);

  // clienti per l'assegnazione a mano (stessa lista del form di creazione)
  useEffect(() => {
    if (!isManager) return;
    authFetch(`${API_BASE}/users`)
      .then((res) => (res && res.ok ? res.json() : []))
      .then((data) => setClientUsers((data || []).filter((u) => u.role === 'cliente')))
      .catch(() => {});
  }, [isManager]);

  useEffect(() => {
    if (!isManager) return;
    loadSummary();
    loadList();
  }, [isManager, loadSummary, loadList]);

  const loadReplyContext = async (id) => {
    try {
      const res = await authFetch(`${API_BASE}/generated-work/${id}/reply`);
      if (res && res.ok) setReply(await res.json());
    } catch {
      // la risposta e' un extra: la pagina funziona lo stesso
    }
  };

  const closeDetail = () => {
    setOpenId(null);
    setDetail(null);
    setReply(null);
    setReplyOpen(false);
    revokePreviews();
  };

  const openDetail = async (id) => {
    if (openId === id) {
      closeDetail();
      return;
    }
    closeDetail();
    setOpenId(id);
    setShowExtracted({});
    setDetailLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/generated-work/${id}`);
      if (!res || !res.ok) throw new Error('Errore nel caricamento della proposta');
      const g = await res.json();
      setDetail(g);
      setAssign({ customerUserId: g.match?.customerUserId?._id || '', applyToSender: true });
      loadReplyContext(id);
      // anteprima automatica di pdf e immagini, anche nelle risposte del cliente
      (g.thread || [])
        .flatMap((t) => t.attachments || [])
        .filter((a) => !a.isInline && a.storedPath && previewKind(a))
        .forEach((a) => anteprima(id, a));
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadSummary(), loadList()]);
    if (openId) {
      const res = await authFetch(`${API_BASE}/generated-work/${openId}`);
      if (res && res.ok) setDetail(await res.json());
      loadReplyContext(openId);
    }
  };

  const scarta = async (id) => {
    const note = window.prompt('Perché scarti questa proposta? (facoltativo)', '');
    if (note === null) return;
    setBusyId(id);
    try {
      const res = await authFetch(`${API_BASE}/generated-work/${id}/review`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'scarta', note }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Errore nello scarto');
      setNotice('Proposta scartata.');
      await refreshAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const riapri = async (id) => {
    setBusyId(id);
    try {
      const res = await authFetch(`${API_BASE}/generated-work/${id}/review`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'riapri' }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Errore nella riapertura');
      setNotice('Proposta riportata tra quelle da revisionare.');
      await refreshAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const rianalizza = async (id) => {
    setBusyId(id);
    setNotice('Rilettura della mail in corso, ci vuole qualche secondo...');
    try {
      const res = await authFetch(`${API_BASE}/generated-work/${id}/reanalyze`, { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Analisi fallita');
      setNotice(
        data?.result?.outcome === 'not_work'
          ? 'Alla rilettura la mail non risulta una richiesta di lavoro: la trovi tra le mail non riconosciute.'
          : 'Proposta aggiornata.'
      );
      await refreshAll();
    } catch (err) {
      setError(err.message);
      setNotice('');
    } finally {
      setBusyId(null);
    }
  };

  const analizzaMail = async (mailId) => {
    setBusyId(mailId);
    setNotice('Analisi in corso, ci vuole qualche secondo...');
    try {
      const res = await authFetch(`${API_BASE}/generated-work/inbound/${mailId}/analyze`, { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Analisi fallita');
      if (data?.outcome === 'analyzed') {
        setNotice('Proposta creata: la trovi tra quelle da revisionare.');
      } else if (data?.outcome === 'not_work') {
        setNotice(`Anche alla rilettura non risulta una richiesta di lavoro (${data.reason || ''}).`);
      } else {
        setNotice(data?.error ? `Analisi fallita: ${data.error}` : 'Analisi eseguita.');
      }
      await refreshAll();
    } catch (err) {
      setError(err.message);
      setNotice('');
    } finally {
      setBusyId(null);
    }
  };

  const fetchAttachmentBlob = async (generatedId, att) => {
    const res = await authFetch(`${API_BASE}/generated-work/${generatedId}/attachments/${att._id}`);
    if (!res || !res.ok) throw new Error('Allegato non disponibile');
    return res.blob();
  };

  const anteprima = async (generatedId, att) => {
    if (previewUrls.current[att._id]) return;
    try {
      const blob = await fetchAttachmentBlob(generatedId, att);
      const url = window.URL.createObjectURL(blob);
      previewUrls.current[att._id] = url;
      setPreviews((p) => ({ ...p, [att._id]: url }));
    } catch (err) {
      setError(err.message);
    }
  };

  const scaricaAllegato = async (generatedId, att) => {
    try {
      const blob = await fetchAttachmentBlob(generatedId, att);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'allegato';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setError(err.message);
    }
  };

  // ---- cliente scelto a mano ----

  const salvaCliente = async (id) => {
    setAssignBusy(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/generated-work/${id}/customer`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assign),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Errore nel salvataggio');
      setNotice(
        assign.customerUserId
          ? `Cliente assegnato${data.updated ? ` anche ad altre ${data.updated} proposte dello stesso mittente` : ''}. Le prossime mail di questo mittente verranno abbinate da sole.`
          : 'Cliente tolto dalla proposta.'
      );
      await refreshAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssignBusy(false);
    }
  };

  // ---- risposta al cliente ----

  const apriRisposta = async (id, { regenerate = false } = {}) => {
    setReplyBusy(true);
    setError('');
    try {
      let ctx = reply;
      if (!ctx) {
        const r = await authFetch(`${API_BASE}/generated-work/${id}/reply`);
        if (r && r.ok) ctx = await r.json();
      }
      let draft = ctx?.draft && ctx.draft.text ? ctx.draft : null;
      if (!draft || regenerate) {
        setNotice('Sto scrivendo la bozza di risposta...');
        const res = await authFetch(
          `${API_BASE}/generated-work/${id}/reply-draft${regenerate ? '?regenerate=1' : ''}`,
          { method: 'POST' }
        );
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.error || 'Bozza non disponibile');
        draft = data.draft;
        setNotice('');
      }
      setReply((prev) => ({ ...(prev || ctx || {}), draft }));
      setReplyForm({
        to: (ctx?.defaults?.to || []).join(', '),
        cc: (ctx?.defaults?.cc || []).join(', '),
        subject: draft.subject || '',
        text: draft.text || '',
      });
      setReplyOpen(true);
    } catch (err) {
      setError(err.message);
      setNotice('');
    } finally {
      setReplyBusy(false);
    }
  };

  const inviaRisposta = async (id) => {
    if (!replyForm.to.trim()) {
      setError('Indica almeno un destinatario.');
      return;
    }
    if (!window.confirm(`Invio la risposta a ${replyForm.to}?`)) return;
    setReplyBusy(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/generated-work/${id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(replyForm),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Invio fallito');
      setNotice(`Risposta inviata da ${data.from} a ${replyForm.to}.`);
      setReplyOpen(false);
      await refreshAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setReplyBusy(false);
    }
  };

  if (!isManager) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-100 px-4">
        <Card className="shadow-md p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Accesso non autorizzato</h2>
          <p className="text-sm text-gray-600 mb-4">
            Solo <strong>Admin</strong>, <strong>Segreteria</strong> e <strong>Gestione</strong> possono
            revisionare le lavorazioni generate.
          </p>
          <button onClick={() => navigate('/lavorazioni')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition">
            Torna alle lavorazioni
          </button>
        </Card>
      </div>
    );
  }

  const tabCount = (key) => {
    if (!summary) return null;
    if (key === 'da_revisionare') return summary.daRevisionare;
    if (key === 'convertita') return summary.convertite;
    if (key === 'scartata') return summary.scartate;
    if (key === 'mail') return (summary.mail?.not_work || 0) + (summary.mail?.error || 0) + (summary.mail?.received || 0);
    return null;
  };

  // ---- pannello risposta ----
  const renderReply = (g) => {
    const replies = reply?.replies || g.replies || [];
    const customerReplies = detail?.customerReplies || [];
    return (
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 text-sm space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-medium text-gray-700">Risposta al cliente</div>
            <div className="text-xs text-gray-500">
              {reply?.from ? `Parte da ${reply.from}` : 'Mittente da configurare'}
              {reply && !reply.dedicated && (
                <span className="text-amber-700"> (casella assistenza non configurata: usa quella di sempre)</span>
              )}
            </div>
          </div>
          {!replyOpen && (
            <button type="button" disabled={replyBusy} onClick={() => apriRisposta(g._id)} className={btn.indigo}>
              {replies.length || customerReplies.length ? 'Rispondi di nuovo' : 'Rispondi al cliente'}
            </button>
          )}
        </div>

        {replyOpen && (
          <div className="space-y-2">
            <div className="grid gap-2 md:grid-cols-2">
              <label className="text-xs text-gray-600">
                A
                <input
                  type="text"
                  value={replyForm.to}
                  onChange={(e) => setReplyForm((f) => ({ ...f, to: e.target.value }))}
                  className="mt-0.5 w-full px-2 py-1.5 border rounded-lg text-sm bg-white"
                />
              </label>
              <label className="text-xs text-gray-600">
                Cc
                <input
                  type="text"
                  value={replyForm.cc}
                  onChange={(e) => setReplyForm((f) => ({ ...f, cc: e.target.value }))}
                  className="mt-0.5 w-full px-2 py-1.5 border rounded-lg text-sm bg-white"
                />
              </label>
            </div>
            <label className="block text-xs text-gray-600">
              Oggetto
              <input
                type="text"
                value={replyForm.subject}
                onChange={(e) => setReplyForm((f) => ({ ...f, subject: e.target.value }))}
                className="mt-0.5 w-full px-2 py-1.5 border rounded-lg text-sm bg-white"
              />
            </label>
            <label className="block text-xs text-gray-600">
              Testo (bozza scritta dal sistema: correggila come vuoi)
              <textarea
                rows={10}
                value={replyForm.text}
                onChange={(e) => setReplyForm((f) => ({ ...f, text: e.target.value }))}
                className="mt-0.5 w-full px-2 py-1.5 border rounded-lg text-sm bg-white font-sans"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={replyBusy} onClick={() => inviaRisposta(g._id)} className={btn.indigo}>
                {replyBusy ? 'Attendi...' : 'Invia'}
              </button>
              <button type="button" disabled={replyBusy} onClick={() => apriRisposta(g._id, { regenerate: true })} className={btn.sky}>
                Rigenera bozza
              </button>
              <button type="button" disabled={replyBusy} onClick={() => setReplyOpen(false)} className={btn.gray}>
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ---- allegati di una mail (originale o risposta del cliente) ----
  const renderAttachments = (g, list) => {
    const attachments = (list || []).filter((a) => !a.isInline);
    if (!attachments.length) return null;
    return (
      <div className="mt-2 space-y-2">
        <div className="text-[11px] uppercase tracking-wide text-gray-400">{attachments.length === 1 ? 'Allegato' : 'Allegati'}</div>
        {attachments.map((a) => {
          const kind = previewKind(a);
          const url = previews[a._id];
          return (
            <div key={a._id} className="rounded border border-gray-200 bg-white p-2">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium text-gray-800">{a.filename}</span>
                <span className="text-gray-400">{formatSize(a.size)}</span>
                {a.extractionStatus === 'done' && <Badge className="bg-emerald-100 text-emerald-700">letto</Badge>}
                {a.extractionStatus === 'pending' && <Badge className="bg-gray-100 text-gray-600">non ancora letto</Badge>}
                {(a.extractionStatus === 'unsupported' || a.extractionStatus === 'error') && (
                  <Badge className="bg-amber-100 text-amber-700" title={a.extractionError}>
                    non letto: {a.extractionError}
                  </Badge>
                )}
                <span className="ml-auto flex gap-2">
                  {a.storedPath && (
                    <button type="button" onClick={() => scaricaAllegato(g._id, a)} className="text-sky-700 underline">
                      scarica
                    </button>
                  )}
                  {a.extractedText && (
                    <button
                      type="button"
                      onClick={() => setShowExtracted((s) => ({ ...s, [a._id]: !s[a._id] }))}
                      className="text-gray-600 underline"
                    >
                      {showExtracted[a._id] ? 'nascondi testo' : 'testo estratto'}
                    </button>
                  )}
                </span>
              </div>
              {kind === 'pdf' && url && (
                <iframe title={a.filename} src={url} className="mt-2 w-full h-[420px] rounded border border-gray-200 bg-white" />
              )}
              {kind === 'image' && url && <img src={url} alt={a.filename} className="mt-2 max-h-[420px] w-auto rounded border border-gray-200" />}
              {kind && !url && a.storedPath && <div className="mt-1 text-[11px] text-gray-400">Carico l'anteprima...</div>}
              {showExtracted[a._id] && (
                <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 text-[11px] text-gray-800 font-sans">
                  {a.extractedText}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ---- dettaglio a due colonne: letto dal sistema | mail originale ----
  const renderDetail = (g) => {
    if (detailLoading || !detail) {
      return <div className="text-xs text-gray-500 px-1 py-2">Caricamento...</div>;
    }
    const p = detail.proposal || {};
    const conf = confidenceBadge(detail.confidence);
    return (
      <div className="mt-3 border-t border-gray-200 pt-3 space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          {/* colonna sinistra: letto dal sistema */}
          <div className="space-y-3">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Cosa ha letto il sistema</h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Azienda" value={p.companyName} />
                <Field label="Luogo" value={p.place} />
                <Field label="Telefono" value={p.customerPhone} />
                <Field label="Email contatto" value={p.customerEmail} />
                <Field label="Tipo impianto" value={p.equipmentType} />
                <Field label="Marca / modello" value={[p.equipmentMaker, p.equipmentModel].filter(Boolean).join(' ')} />
                <Field label="Matricola" value={p.equipmentSerial} />
                <Field label="Inventario" value={p.equipmentInventory} />
                <Field label="Rif. ordine cliente" value={p.rifOrdineCliente} />
                <Field label="Rif. preventivo" value={p.rifPreventivo} />
                <Field label="Date richieste" value={(p.requestedDates || []).map(formatDate).join(', ')} />
              </div>
              {p.description && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wide text-gray-400">Descrizione</div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">{p.description}</div>
                </div>
              )}
            </div>

            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm">
              <div className="font-medium text-gray-700 mb-1">Anagrafica</div>
              <div className={detail.match?.customerUserId ? 'text-gray-800' : 'text-amber-700'}>{matchLabel(detail.match)}</div>
              {detail.reviewStatus !== 'convertita' && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex flex-wrap gap-2 items-center">
                    <select
                      value={assign.customerUserId}
                      onChange={(e) => setAssign((a) => ({ ...a, customerUserId: e.target.value }))}
                      className="flex-1 min-w-[12rem] px-2 py-1.5 border rounded-lg text-xs bg-white"
                    >
                      <option value="">— Nessun cliente —</option>
                      {clientUsers.map((c) => (
                        <option key={c._id} value={c._id}>
                          {(c.nome || '').trim() || c.username} ({c.matricola || 'C****'})
                        </option>
                      ))}
                    </select>
                    <button type="button" disabled={assignBusy} onClick={() => salvaCliente(g._id)} className={btn.skySolid}>
                      Salva cliente
                    </button>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={assign.applyToSender}
                      onChange={(e) => setAssign((a) => ({ ...a, applyToSender: e.target.checked }))}
                    />
                    anche alle altre proposte da revisionare di questo mittente
                  </label>
                </div>
              )}
              {detail.match?.machineId && (
                <div className="text-gray-800 mt-1">
                  Macchina: {detail.match.machineId.nome}
                  {detail.match.machineId.sede ? ` (${detail.match.machineId.sede})` : ''}
                  {detail.match.machineId.matricola ? `, matricola ${detail.match.machineId.matricola}` : ''}
                </div>
              )}
            </div>

            <div className={`rounded-lg border p-3 text-sm ${detail.ambiguities?.length ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="font-medium text-gray-700 mb-1">Da verificare</div>
              {detail.ambiguities?.length ? (
                <ul className="list-disc pl-4 space-y-0.5 text-amber-800">
                  {detail.ambiguities.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-emerald-800">Il modello non ha segnalato dubbi.</div>
              )}
              <div className="mt-2">
                <Badge className={conf.cls}>{conf.label}</Badge>
                {detail.model && <span className="ml-2 text-[11px] text-gray-400">modello {detail.model}</span>}
              </div>
            </div>

            {renderReply(g)}
          </div>

          {/* colonna destra: la conversazione com'e' arrivata */}
          <div className="rounded-lg border border-gray-200 p-3 text-sm space-y-3 lg:sticky lg:top-4 self-start">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Mail e conversazione</h4>
            {(detail.thread || []).map((t, i) => (
              <div
                key={t.id || i}
                className={`rounded-lg border p-2 ${
                  t.kind === 'out' ? 'border-indigo-200 bg-indigo-50/40' : t.root ? 'border-gray-200' : 'border-amber-200 bg-amber-50/40'
                }`}
              >
                <div className="text-[11px] uppercase tracking-wide text-gray-400">
                  {t.kind === 'out' ? 'Risposta di Ciesse' : t.root ? 'Mail del cliente' : 'Risposta del cliente'} · {formatDateTime(t.date)}
                </div>
                <div className="text-sm font-medium text-gray-800">{t.subject || '(senza oggetto)'}</div>
                <div className="text-xs text-gray-500">{t.kind === 'out' ? `A ${(t.to || []).join(', ')}` : `Da ${t.from}`}</div>
                <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-white/70 p-2 text-xs text-gray-800 font-sans">
                  {t.text || '(corpo vuoto)'}
                </pre>
                {renderAttachments(g, t.attachments)}
              </div>
            ))}
          </div>
        </div>

        {(detail.reviewedAt || detail.reviewNote) && (
          <div className="text-xs text-gray-500">
            {detail.reviewStatus === 'convertita' ? 'Convertita' : 'Scartata'} da {userLabel(detail.reviewedBy)} il {formatDateTime(detail.reviewedAt)}
            {detail.reviewNote ? ` · ${detail.reviewNote}` : ''}
          </div>
        )}
      </div>
    );
  };

  const renderProposal = (g) => {
    const mail = g.inboundMail || {};
    const p = g.proposal || {};
    const conf = confidenceBadge(g.confidence);
    const isOpen = openId === g._id;
    const busy = busyId === g._id;

    return (
      <Card key={g._id} className="shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge className={typeBadge(p.type)}>{p.type || 'tipo da definire'}</Badge>
              {p.urgency && <Badge className={urgencyBadge(p.urgency)}>urgenza {p.urgency}</Badge>}
              <Badge className={conf.cls}>{conf.label}</Badge>
              {g.ambiguities?.length > 0 && (
                <Badge className="bg-amber-100 text-amber-700">
                  {g.ambiguities.length} {g.ambiguities.length === 1 ? 'cosa da verificare' : 'cose da verificare'}
                </Badge>
              )}
              {g.repliedAt && <Badge className="bg-indigo-100 text-indigo-700">risposto {formatDate(g.repliedAt)}</Badge>}
              {g.lastCustomerReplyAt && (
                <Badge className="bg-amber-100 text-amber-700">risposta del cliente {formatDate(g.lastCustomerReplyAt)}</Badge>
              )}
              {g.workOrder?.internalCode && <Badge className="bg-gray-800 text-white">{g.workOrder.internalCode}</Badge>}
            </div>
            <div className="text-sm font-semibold text-gray-800">{g.summary || mail.subject || '(senza oggetto)'}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {p.companyName ? `${p.companyName} · ` : ''}
              {senderLabel(mail)} · {formatDateTime(mail.sentAt || mail.receivedAt)}
              {mail.attachments?.filter((a) => !a.isInline).length > 0 && (
                <span> · {mail.attachments.filter((a) => !a.isInline).length} allegati</span>
              )}
            </div>
            {!g.match?.customerUserId && <div className="text-xs text-amber-700 mt-1">Cliente non trovato in anagrafica</div>}
          </div>

          <div className="flex flex-wrap gap-2 md:justify-end shrink-0">
            <button type="button" onClick={() => openDetail(g._id)} className={btn.gray}>
              {isOpen ? 'Chiudi' : 'Dettagli e confronto'}
            </button>
            {g.reviewStatus === 'da_revisionare' && (
              <>
                <button type="button" disabled={busy} onClick={() => navigate(`/lavorazioni/crea?generata=${g._id}`)} className={btn.green}>
                  Crea lavorazione
                </button>
                <button type="button" disabled={busy} onClick={() => rianalizza(g._id)} className={btn.sky}>
                  Rileggi mail
                </button>
                <button type="button" disabled={busy} onClick={() => scarta(g._id)} className={btn.red}>
                  Scarta
                </button>
              </>
            )}
            {g.reviewStatus === 'scartata' && (
              <button type="button" disabled={busy} onClick={() => riapri(g._id)} className={btn.amber}>
                Riapri
              </button>
            )}
          </div>
        </div>
        {isOpen && renderDetail(g)}
      </Card>
    );
  };

  const renderMail = (m) => {
    const busy = busyId === m._id;
    const attachments = (m.attachments || []).filter((a) => !a.isInline);
    return (
      <Card key={m._id} className="shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {m.status === 'not_work' && <Badge className="bg-gray-100 text-gray-600">{m.notWorkReason || 'non e\' una richiesta'}</Badge>}
              {m.status === 'error' && (
                <Badge className="bg-red-100 text-red-700">
                  errore ({m.analysisAttempts} {m.analysisAttempts === 1 ? 'tentativo' : 'tentativi'})
                </Badge>
              )}
              {m.status === 'received' && <Badge className="bg-sky-100 text-sky-700">in attesa</Badge>}
              {attachments.length > 0 && (
                <Badge className="bg-gray-100 text-gray-600">
                  {attachments.length} {attachments.length === 1 ? 'allegato' : 'allegati'}
                </Badge>
              )}
            </div>
            <div className="text-sm font-semibold text-gray-800">{m.subject || '(senza oggetto)'}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {senderLabel(m)} · {formatDateTime(m.sentAt || m.receivedAt)}
            </div>
            {m.status === 'error' && m.analysisError && <div className="text-xs text-red-700 mt-1 break-words">{m.analysisError}</div>}
          </div>
          <div className="flex flex-wrap gap-2 md:justify-end shrink-0">
            <button type="button" disabled={busy} onClick={() => analizzaMail(m._id)} className={btn.skySolid}>
              {m.status === 'not_work' ? 'Analizza comunque' : 'Analizza ora'}
            </button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-6xl space-y-4">
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">Lavorazioni generate</h1>
              <p className="text-xs md:text-sm text-gray-500">
                Richieste lette in automatico dalla casella assistenza. Confronta quello che ha capito il sistema con la
                mail e gli allegati, poi crea la lavorazione con il form di sempre, rispondi al cliente o scarta la
                proposta.
              </p>
              {summary?.cost && (
                <p className="text-[11px] text-gray-400 mt-1">
                  Lettura OpenAI: {summary.cost.analyzed + summary.cost.estimatedMails} mail analizzate, spesa circa{' '}
                  {summary.cost.estimatedUsd.toFixed(2).replace('.', ',')} $
                  {summary.cost.estimatedMails > 0 ? ' (in parte stimata)' : ''}
                </p>
              )}
            </div>
            <button onClick={() => navigate('/lavorazioni')} className="hidden md:inline-flex px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200">
              ← Torna alle lavorazioni
            </button>
          </div>
        </Card>

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const n = tabCount(t.key);
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                  closeDetail();
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  active ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t.label}
                {n !== null && n !== undefined && <span className={`ml-1.5 ${active ? 'text-gray-300' : 'text-gray-400'}`}>{n}</span>}
              </button>
            );
          })}
          <button type="button" onClick={refreshAll} className="ml-auto px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-50">
            Aggiorna
          </button>
        </div>

        {/* filtri */}
        <Card className="shadow-sm px-3 py-2">
          <form
            className="flex flex-wrap items-end gap-2 text-xs"
            onSubmit={(e) => {
              e.preventDefault();
              setFilters((f) => ({ ...f, q: pendingQ.trim() }));
            }}
          >
            <label className="text-gray-600">
              Dal
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                className="block mt-0.5 px-2 py-1.5 border rounded-lg bg-white"
              />
            </label>
            <label className="text-gray-600">
              Al
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                className="block mt-0.5 px-2 py-1.5 border rounded-lg bg-white"
              />
            </label>
            <label className="text-gray-600 flex-1 min-w-[10rem]">
              Cerca (azienda, mittente, oggetto, luogo)
              <input
                type="text"
                value={pendingQ}
                onChange={(e) => setPendingQ(e.target.value)}
                placeholder="es. Rossi, Perugia, cella"
                className="block mt-0.5 w-full px-2 py-1.5 border rounded-lg bg-white"
              />
            </label>
            {tab !== 'mail' && (
              <label className="text-gray-600">
                Cliente
                <select
                  value={filters.customer}
                  onChange={(e) => setFilters((f) => ({ ...f, customer: e.target.value }))}
                  className="block mt-0.5 px-2 py-1.5 border rounded-lg bg-white"
                >
                  <option value="">tutti</option>
                  <option value="matched">abbinato</option>
                  <option value="missing">non trovato</option>
                </select>
              </label>
            )}
            <button type="submit" className={btn.gray}>
              Cerca
            </button>
            {(filters.from || filters.to || filters.q || filters.customer) && (
              <button
                type="button"
                onClick={() => {
                  setPendingQ('');
                  setFilters({ from: '', to: '', q: '', customer: '' });
                }}
                className={btn.gray}
              >
                Azzera filtri
              </button>
            )}
            {!loading && (
              <span className="text-gray-400 ml-auto">
                {tab === 'mail' ? mails.length : items.length} risultati
              </span>
            )}
          </form>
        </Card>

        {tab === 'mail' && (
          <div className="flex flex-wrap gap-2 text-xs">
            {MAIL_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setMailFilter(f.key)}
                className={`px-2.5 py-1 rounded-full ${
                  mailFilter === f.key ? 'bg-sky-100 text-sky-700 font-semibold' : 'bg-white border border-gray-200 text-gray-600'
                }`}
              >
                {f.label}
                {summary?.mail && <span className="ml-1 text-gray-400">{summary.mail[f.key] || 0}</span>}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex justify-between gap-2">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} className="text-xs underline">chiudi</button>
          </div>
        )}
        {notice && (
          <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-800 flex justify-between gap-2">
            <span>{notice}</span>
            <button type="button" onClick={() => setNotice('')} className="text-xs underline">chiudi</button>
          </div>
        )}

        {loading ? (
          <Card className="shadow-sm p-6 text-sm text-gray-500 text-center">Caricamento...</Card>
        ) : tab === 'mail' ? (
          mails.length === 0 ? (
            <Card className="shadow-sm p-6 text-sm text-gray-500 text-center">Nessuna mail in questo stato.</Card>
          ) : (
            <div className="space-y-3">{mails.map(renderMail)}</div>
          )
        ) : items.length === 0 ? (
          <Card className="shadow-sm p-6 text-sm text-gray-500 text-center">
            {tab === 'da_revisionare'
              ? 'Nessuna proposta da revisionare. Quando arriva una richiesta via mail la trovi qui.'
              : 'Nessuna proposta in questo stato.'}
          </Card>
        ) : (
          <div className="space-y-3">{items.map(renderProposal)}</div>
        )}

        <button onClick={() => navigate('/lavorazioni')} className="md:hidden mt-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200">
          ← Torna alle lavorazioni
        </button>
      </div>
    </div>
  );
}
